import { useState } from "react";
import { SetupAction } from "@/hooks/agent/useGenieSetupReducer";

interface TextMessageHandlerProps {
  connected: boolean;
  isConnecting: boolean;
  getService: () => any;
  handleConversationToggleWithWakeLock: () => Promise<void>;
  dispatch: React.Dispatch<SetupAction>;
  attachments: any[];
  consumeReadyContext: () => string | null;
  clearAttachments: () => void;
  onMessageSent?: () => void;
}

export const useTextMessageHandler = ({
  connected,
  isConnecting,
  getService,
  handleConversationToggleWithWakeLock,
  dispatch,
  attachments,
  consumeReadyContext,
  clearAttachments,
  onMessageSent,
}: TextMessageHandlerProps) => {
  const [inlineMessage, setInlineMessage] = useState("");

  const handleInlineSend = async (
    onChatModalOpen: (startedFromText: boolean) => void,
    onTextOnlyModeSet: (enabled: boolean) => void,
  ) => {
    const trimmed = inlineMessage.trim();
    if (!trimmed && attachments.length === 0) {
      return;
    }

    // Ensure conversation is active; when starting from text, prefer text-only mode
    if (!connected && !isConnecting) {
      // Set overrides before starting conversation
      const service = getService();
      if (service) {
        service.setOverrides({
          conversation: { textOnly: true },
        });
      }
      try {
        await handleConversationToggleWithWakeLock();
      } catch (error) {
        console.error("Error starting conversation:", error);
        return;
      }
      onTextOnlyModeSet(true);
      onChatModalOpen(true);
    } else if (connected) {
      // If conversation is already active, open chat modal
      onChatModalOpen(false);
      onTextOnlyModeSet(true);
    }

    const service = getService();
    if (!service) {
      return;
    }

    // Send contextual analysis text for image attachments (if available) before the message
    const analysisContext = consumeReadyContext();

    if (analysisContext) {
      service.sendUserMessage(
        `User attached images with the following descriptions:\n\n${analysisContext}`,
      );
    }

    // Add an image message into the local chat transcript
    if (attachments.length > 0) {
      const dataUrlPromises = attachments.map(
        (att) =>
          new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === "string") {
                resolve(reader.result);
              } else {
                resolve(null);
              }
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(att.file);
          }),
      );

      const dataUrls = await Promise.all(dataUrlPromises);

      const imageMessage: any = {
        message: "",
        isUser: true,
        timestamp: new Date(),
        imageAttachments: attachments.map((att, index) => ({
          id: att.id,
          previewUrl: dataUrls[index] ?? att.previewUrl,
          analysisText: att.analysisText,
        })),
      };

      const newMessage = {
        role: "user" as const,
        message: imageMessage.message,
        timestamp: Date.now(),
        imageAttachments: imageMessage.imageAttachments,
      };
      dispatch({ type: "ADD_MESSAGE", payload: newMessage });
    }

    if (trimmed) {
      const userMessage = { role: "user" as const, message: trimmed, timestamp: Date.now() };
      dispatch({ type: "ADD_MESSAGE", payload: userMessage });
      service.sendUserMessage(trimmed);
    }

    if (attachments.length > 0) {
      clearAttachments();
    }

    setInlineMessage("");
    onMessageSent?.();
  };

  return {
    inlineMessage,
    setInlineMessage,
    handleInlineSend,
  };
};
